import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import axios from "axios";
import { useAuth } from "../../Context/AuthContext";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Papa from "papaparse";
import ToastContext from "../../Context/ToastContext";
import MultiAxisChartFilter from "./MultiAxisChartFilter";
import html2canvas from "html2canvas";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export const options = {
  responsive: true,
  interaction: {
    mode: "index",
    intersect: false,
  },
  stacked: false,
  plugins: {
    legend: {
      display: true,
      position: "right",
      align: "center",
      labels: {
        boxWidth: 15,
        boxHeight: 15,
        padding: 15,
      },
    },
    title: {
      display: false,
      text: "Multi Axis",
    },
    datalabels: {
      display: false,
    },
  },
  scales: {
    y: {
      type: "linear",
      display: true,
      position: "left",
    },
    y1: {
      type: "linear",
      display: true,
      position: "right",
      grid: {
        drawOnChartArea: false,
      },
    },
  },
  layout: {
    padding: {
      bottom: 50,
      left: 10,
      top: 0,
      right: 10,
    },
  },
};

const MultiAxisLineChart = ({ meterConfig }) => {
  const { token } = useAuth();
  const URL = process.env.REACT_APP_URL;
  const { toast } = useContext(ToastContext);
  const [MapData, setData] = useState([]);
  const [dateFilter, setDateFilter] = useState({
    days: "",
    startdate: "",
    enddate: "",
  });
  const [dateError, setDateError] = useState(false);
  const [authorized, setAuthorized] = useState(true);
  const fetchData = useCallback(async () => {
    if (dateFilter.startdate && !dateFilter.enddate && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Please select end date");
    }
    if (!dateFilter.startdate && !dateFilter.days && dateFilter.enddate) {
      setDateError(true);
      setData([]);
      return toast.error("Please select start date");
    }
    if (dateFilter.startdate > dateFilter.enddate && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Start date should be date earlier than end date");
    }
    if (new Date() < new Date(dateFilter.startdate) && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Please select start date earlier than curren date");
    }
    setDateError(false);
    let url = `${URL}axis-chart?days=${dateFilter.days}&startDate=${dateFilter.startdate}&endDate=${dateFilter.enddate}`;
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `abc ${token}`,
        },
      });
      setData(res?.data);
    } catch (err) {
      if (err.response.data.message === "Unauthorized") setAuthorized(false);
      console.log(err);
    }
  }, [dateFilter]);

  useEffect(() => {
    fetchData();
  }, []);

  const setTrue =
    dateFilter.days.length ||
    (dateFilter.startdate.length && dateFilter.enddate.length);

  const label1 =
    MapData?.data?.M1[0]?.D &&
    MapData?.data?.M1.map((item) => {
      const dateObj = new Date(item?.D);
      return format(dateObj, "dd/MM");
    });

  const label2 =
    MapData?.data?.M1[0]?.T &&
    MapData?.data?.M1.map((item) => {
      const timeString = item?.T;
      return format(new Date(`1970-01-01T${timeString}`), "HH:mm");
    });

  const customDateData = {
    labels: label1,
    datasets: meterConfig?.map((meter) => ({
      label: meter.label,
      data: MapData?.data?.[meter.key]?.map(
        (item) => item?.[`${meter.key}TVDifference`]
      ),

      borderColor: meter.borderColor,
      backgroundColor: meter.backgroundColor,
      yAxisID: meter.yAxisID,
    })),
  };

  const defaultData = {
    labels: label2,
    datasets: meterConfig?.map((meter) => ({
      label: meter.label,
      data: MapData?.data?.[meter.key]?.map((item) => item?.[`${meter.key}TV`]),
      borderColor: meter.borderColor,
      backgroundColor: meter.backgroundColor,
      yAxisID: meter.yAxisID,
    })),
  };

  const handleCSVDownload = () => {
    if (MapData?.data) {
      const csvData = MapData?.data?.M1.map((item, index) => {
        let DateTime;
        if (setTrue === 0) {
          DateTime = item.T;
        } else {
          DateTime = item.D;
        }
        const rowData = {
          DateTime,
          "JLR | Drinking Water": MapData.data.M1[index]?.M1TV || 0,
          "K Block | Drinking Water": MapData.data.M2[index]?.M2TV || 0,
          "K Block | Process Water": MapData.data.M3[index]?.M3TV || 0,
          "MIDC | Fresh Water 2": MapData.data.M4[index]?.M4TV || 0,
          "MIDC | Fresh Water 1": MapData.data.M5[index]?.M5TV || 0,
          "J Block | Drinking Water": MapData.data.M6[index]?.M6TV || 0,
          "ETP | RO Reject": MapData.data.M7[index]?.M7TV || 0,
          "ETP | ACF 2 Out": MapData.data.M8[index]?.M8TV || 0,
          "ETP | JLR Garden": MapData.data.M9[index]?.M9TV || 0,
          "ETP | RO Transfer": MapData.data.M10[index]?.M10TV || 0,
          "JLRD | JLR Drinking":
            (MapData.data.M43 && MapData.data.M43[index]?.M43TV) || 0,
        };

        if (setTrue !== 0) {
          rowData["JLR | Drinking Water"] =
            MapData.data.M1[index]?.M1TVDifference || 0;
          rowData["K Block | Drinking Water"] =
            MapData.data.M2[index]?.M2TVDifference || 0;
          rowData["K Block | Process Water"] =
            MapData.data.M3[index]?.M3TVDifference || 0;
          rowData["MIDC | Fresh Water 2"] =
            MapData.data.M4[index]?.M4TVDifference || 0;
          rowData["MIDC | Fresh Water 1"] =
            MapData.data.M5[index]?.M5TVDifference || 0;
          rowData["J Block | Drinking Water"] =
            MapData.data.M6[index]?.M6TVDifference || 0;
          rowData["ETP | RO Reject"] =
            MapData.data.M7[index]?.M7TVDifference || 0;
          rowData["ETP | ACF 2 Out"] =
            MapData.data.M8[index]?.M8TVDifference || 0;
          rowData["ETP | JLR Garden"] =
            MapData.data.M9[index]?.M9TVDifference || 0;
          rowData["ETP | RO Transfer"] =
            MapData.data.M10[index]?.M10TVDifference || 0;
          //
          rowData["JLRD | JLR Drinking"] =
            (MapData.data.M43 && MapData.data.M43[index]?.M43TVDifference) || 0;
        }
        return rowData;
      });

      const csvBlob = new Blob([Papa.unparse(csvData, { headers: true })], {
        type: "text/csv;charset=utf-8;",
      });

      saveAs(csvBlob, "Data.csv");
      toast.success("Data downloading, please wait...");
    }
  };

  const handlePDFDownload = async () => {
    const headers = [
      setTrue === 0 ? "Time" : "Date",
      "JLR | Drinking Water",
      "K Block  | Drinking Water",
      "K Block | Process Water",
      "MIDC | Fresh Water 2",
      "MIDC | Fresh Water 1",
      "J Block | Drinking Water",
      "ETP | RO Reject",
      "ETP | ACF 2 Out",
      "ETP | JLR Garden",
      "ETP | RO Transfer",
      "JLRD | JLR Drinking",
    ];

    const pdfData = MapData?.data;
    const rows = [];

    if (setTrue === 0) {
      for (let i = 0; i < pdfData.M1.length; i++) {
        const timeWithSeconds = pdfData.M1[i]?.T;
        const time = timeWithSeconds?.split(":")?.slice(0, 2)?.join(":");
        const row = [
          time,
          pdfData.M1[i]?.M1TV?.toFixed(),
          pdfData.M2[i]?.M2TV?.toFixed(),
          pdfData.M3[i]?.M3TV?.toFixed(),
          pdfData.M4[i]?.M4TV?.toFixed(),
          pdfData.M5[i]?.M5TV?.toFixed(),
          pdfData.M6[i]?.M6TV?.toFixed(),
          pdfData.M7[i]?.M7TV?.toFixed(),
          pdfData.M8[i]?.M8TV?.toFixed(),
          pdfData.M9[i]?.M9TV?.toFixed(),
          pdfData.M10[i]?.M10TV?.toFixed(),
          pdfData.M43 && pdfData.M43[i]?.M43TV?.toFixed(),
        ];
        rows.push(row);
      }
    } else {
      for (let i = 0; i < pdfData.M1.length; i++) {
        const date = format(new Date(pdfData.M1[i]?.D), "dd/MM/yyyy");
        const row = [
          date,
          pdfData.M1[i]?.M1TVDifference?.toFixed(),
          pdfData.M2[i]?.M2TVDifference?.toFixed(),
          pdfData.M3[i]?.M3TVDifference?.toFixed(),
          pdfData.M4[i]?.M4TVDifference?.toFixed(),
          pdfData.M5[i]?.M5TVDifference?.toFixed(),
          pdfData.M6[i]?.M6TVDifference?.toFixed(),
          pdfData.M7[i]?.M7TVDifference?.toFixed(),
          pdfData.M8[i]?.M8TVDifference?.toFixed(),
          pdfData.M9[i]?.M9TVDifference?.toFixed(),
          pdfData.M10 && pdfData.M10[i]?.M10TVDifference?.toFixed(),
          //
          pdfData.M11 && pdfData.M11[i]?.M11TVDifference?.toFixed(),
          pdfData.M12 && pdfData.M12[i]?.M12TVDifference?.toFixed(),
          pdfData.M13 && pdfData.M13[i]?.M13TVDifference?.toFixed(),
          pdfData.M14 && pdfData.M14[i]?.M14TVDifference?.toFixed(),
          pdfData.M26 && pdfData.M26[i]?.M26TVDifference?.toFixed(),
          pdfData.M43 && pdfData.M43[i]?.M43TVDifference?.toFixed(),
        ];
        rows.push(row);
      }
    }
    const date = new Date().toLocaleString();
    const doc = new jsPDF("l", "pt", "letter");

    let y = 10;
    doc.setLineWidth(2);
    doc.text(350, (y += 30), "Report");

    const chart = document.getElementById("lineChart");
    chart.style.backgroundColor = "#F9F9F9";
    chart.style.paddingTop = "40px";
    chart.style.borderRadius = "10px";

    const canvas = await html2canvas(chart);
    const imgData = canvas.toDataURL("image/jpeg");
    doc.addImage(
      imgData,
      "JPEG",
      40,
      (y += 20),
      doc.internal.pageSize.getWidth() - 70,
      250
    );
    doc.setFontSize(8);
    doc.text(`Downloaded at: ${date}`, 170, 22, null, null, "right");
    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 340,
      theme: "grid",
      tableWidth: "wrap",
      headStyles: { fontSize: 8, halign: "center" },
      columnStyles: {
        0: { halign: "center", cellWidth: 55, fontSize: 8 },
        1: { halign: "center", cellWidth: 55, fontSize: 8 },
        2: { halign: "center", cellWidth: 55, fontSize: 8 },
        3: { halign: "center", cellWidth: 55, fontSize: 8 },
        4: { halign: "center", cellWidth: 55, fontSize: 8 },
        5: { halign: "center", cellWidth: 55, fontSize: 8 },
        6: { halign: "center", cellWidth: 55, fontSize: 8 },
        7: { halign: "center", cellWidth: 55, fontSize: 8 },
        8: { halign: "center", cellWidth: 55, fontSize: 8 },
        9: { halign: "center", cellWidth: 55, fontSize: 8 },
        10: { halign: "center", cellWidth: 55, fontSize: 8 },
        11: { halign: "center", cellWidth: 55, fontSize: 8 },
      },
    });
    doc.save("Data.pdf");
    toast.success("Data downloading, please wait...");
  };

  const DataDownload = async () => {
    if (!authorized) {
      return toast.error("Unauthorized");
    }
    if (dateFilter.startdate && !dateFilter.enddate && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Please select end date");
    }
    if (!dateFilter.startdate && !dateFilter.days && dateFilter.enddate) {
      setDateError(true);
      setData([]);
      return toast.error("Please select start date");
    }
    if (dateFilter.startdate > dateFilter.enddate && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Start date should be date earlier than end date");
    }
    if (new Date() < new Date(dateFilter.startdate) && !dateFilter.days) {
      setDateError(true);
      setData([]);
      return toast.error("Please select start date earlier than current date");
    }
    if (dateError) {
      return toast.error("Data not available for download");
    }
    handleCSVDownload();
    await handlePDFDownload();
    const chart = document.getElementById("lineChart");
    chart.style.backgroundColor = "";
    chart.style.paddingTop = "";
    chart.style.borderRadius = "";
  };

  return (
    <>
      <div style={{ margin: "auto" }}>
        <MultiAxisChartFilter
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          fetchData={fetchData}
          DataDownload={DataDownload}
        />
        {MapData?.data ? (
          <Line
            id="lineChart"
            options={options}
            data={setTrue === 0 ? defaultData : customDateData}
            height={400}
            width={1350}
          />
        ) : (
          <Line
            id="lineChart"
            options={options}
            data={customDateData}
            height={400}
            width={1350}
          />
        )}
      </div>
    </>
  );
};
export default MultiAxisLineChart;
